import {ButtonIcon, COLOR, Progress, Text, View} from '@lookiero/aurora';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {ModalStepper} from '../modalStepper/ModalStepper';
import style from './Stepper.style';
import {getActiveStep} from '../stepper';
import {BoxPreviewStatus} from '@modules/box/core/projection/boxPreview/boxPreview';
import {TouchableOpacity} from 'react-native';
import {TEXT} from './Stepper.definition';
import {OrderStatus} from '@domain/model/Order';
import {eventManager} from '@application/Event';
import {MyLookieroActionEvent} from '@domain/model/Event';
import {PageEventView} from '@domain/model/Event/domain/model/UiEvents';
import {CheckoutProjection} from '@lookiero/checkout/dist/src/projection/checkout/checkout';

export interface StepperProps {
  readonly checkout: CheckoutProjection | undefined;
  readonly orderStatus: keyof typeof OrderStatus | undefined;
  readonly orderNextBoxDate?: string | undefined;
  readonly boxPreviewStatus?: BoxPreviewStatus;
  readonly rounded: boolean;
}

const PROGRESS_BAR_LENGTH = 5;

const Stepper: FC<StepperProps> = ({checkout, orderStatus, boxPreviewStatus, orderNextBoxDate, rounded}) => {
  const {formatMessage} = useIntl();
  const [modalVisible, setModalVisible] = useState(false);

  const activeStep = getActiveStep({checkout, orderStatus});

  const stepperTexts = useMemo(() => {
    if (checkout) {
      return {
        title: formatMessage(TEXT.STEP_5_TITLE),
        subtitle: formatMessage(TEXT.SUBTITLE_DELIVERED),
        description: formatMessage(TEXT.STEP_5_DESCRIPTION),
      };
    }

    if (orderStatus === OrderStatus.BORRADOR || orderStatus === OrderStatus.PEDIDO_REALIZADO) {
      return {
        title: formatMessage(TEXT.STEP_1_TITLE),
        subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
        description: formatMessage(TEXT.STEP_1_DESCRIPTION),
      };
    }
    if (orderStatus === OrderStatus.EN_MARCHA) {
      return {
        title: formatMessage(TEXT.STEP_2_TITLE),
        subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
        description: formatMessage(TEXT.STEP_2_DESCRIPTION),
      };
    }
    if (orderStatus === OrderStatus.EN_PREPARACION) {
      if (boxPreviewStatus === BoxPreviewStatus.NOTIFIED) {
        return {
          title: formatMessage(TEXT.STEP_3_TITLE_PREVIEW_NOTIFIED),
          subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
          description: formatMessage(TEXT.STEP_3_DESCRIPTION_PREVIEW_NOTIFIED),
        };
      }
      if (boxPreviewStatus === BoxPreviewStatus.SUBMITTED) {
        return {
          title: formatMessage(TEXT.STEP_3_TITLE_PREVIEW_SUBMITTED),
          subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
          description: formatMessage(TEXT.STEP_3_DESCRIPTION_PREVIEW_SUBMITTED),
        };
      }
      if (boxPreviewStatus === BoxPreviewStatus.EXPIRED) {
        return {
          title: formatMessage(TEXT.STEP_3_TITLE_PREVIEW_EXPIRED),
          subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
          description: formatMessage(TEXT.STEP_3_DESCRIPTION_PREVIEW_EXPIRED),
        };
      }

      return {
        title: formatMessage(TEXT.STEP_3_TITLE_PREVIEW_SEND),
        subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
        description: formatMessage(TEXT.STEP_3_DESCRIPTION_PREVIEW_SEND),
      };
    }
    if (orderStatus === OrderStatus.ENVIADO) {
      return {
        title: formatMessage(TEXT.STEP_4_TITLE),
        subtitle: formatMessage(TEXT.SUBTITLE_DELIVERY_DATE, {date: orderNextBoxDate}),
        description: formatMessage(TEXT.STEP_4_DESCRIPTION),
      };
    }
  }, [boxPreviewStatus, checkout, formatMessage, orderNextBoxDate, orderStatus]);

  const stepperPressHandler = useCallback((): void => {
    setModalVisible(true);
    eventManager.emit(MyLookieroActionEvent.STEPPER_CLICK, {view: PageEventView.PLACED_ORDER});
  }, []);

  return stepperTexts ? (
    <TouchableOpacity
      testID="my-lk-stepper"
      style={[style.wrapper, rounded && style.rounded]}
      onPress={stepperPressHandler}
      activeOpacity={1}
    >
      <Text action level={2} color={COLOR.BG_SUCCESS}>
        {stepperTexts.title}
      </Text>
      <Text detail level={1} color={COLOR.TEXT_MEDIUM}>
        {stepperTexts.subtitle}
      </Text>
      <Progress
        active={activeStep}
        activeColor={COLOR.BG_SUCCESS}
        inactiveColor={COLOR.TEXT_DISABLED}
        length={PROGRESS_BAR_LENGTH}
        style={style.progressBar}
      />
      <Text detail level={1} style={style.information}>
        {stepperTexts.description}
      </Text>
      <View style={style.infoBox}>
        <ButtonIcon small name="info_small" style={style.infoIcon} color={COLOR.TEXT_MEDIUM} />
        <Text detail level={2} underlined color={COLOR.TEXT_MEDIUM}>
          {formatMessage(TEXT.MODAL_LINK)}
        </Text>
      </View>
      <ModalStepper
        isVisible={modalVisible}
        onClose={() => setModalVisible(false)}
        checkout={checkout}
        orderStatus={orderStatus as keyof typeof OrderStatus}
        boxPreviewStatus={boxPreviewStatus}
      />
    </TouchableOpacity>
  ) : null;
};

export {Stepper};
